import { useState, useEffect } from 'react';
import { Image as ImageIcon, Video, X } from 'lucide-react';

import { isPrerendering } from '../services/api';

import { Card, CardContent } from './ui/card';

const GalleryPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(!isPrerendering());
  const [selectedItem, setSelectedItem] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'image', 'video'

  useEffect(() => {
    // Skip API calls during pre-rendering (react-snap)
    if (isPrerendering()) return;

    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gallery`);
      if (response.ok) {
        const data = await response.json();
        setItems(data);
      }
    } catch (error) {
      console.error('Failed to load gallery');
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = filter === 'all' 
    ? items 
    : items.filter(item => item.media_type === filter);

  const Lightbox = ({ item, onClose }) => {
    if (!item) return null;

    return (
      <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4" onClick={onClose}>
        <div className="relative max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={onClose}
            className="absolute -top-12 right-0 text-white hover:text-gray-300"
          >
            <X className="h-8 w-8" />
          </button>

          {item.media_type === 'image' ? (
            <img 
              src={item.media_url} 
              alt={item.title}
              className="w-full h-auto max-h-[80vh] object-contain"
            />
          ) : (
            <div className="aspect-video bg-black">
              {item.media_url.includes('youtube.com') || item.media_url.includes('youtu.be') ? (
                <iframe
                  src={item.media_url}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={item.title}
                ></iframe>
              ) : (
                <video
                  src={item.media_url}
                  controls
                  className="w-full h-full"
                >
                  Your browser does not support the video tag.
                </video>
              )}
            </div>
          )}

          <div className="bg-white p-4 mt-2">
            <h3 className="text-xl font-semibold">{item.title}</h3>
            {item.description && (
              <p className="text-gray-600 mt-2">{item.description}</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="flex justify-center p-20">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Hero Banner */}
      <section className="bg-gradient-to-r from-red-600 to-red-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Gallery
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto">
              Photos and videos from our training sessions, events, and community
            </p>
          </div>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="py-8 bg-white shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => setFilter('all')}
              className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                filter === 'all' 
                  ? 'bg-red-600 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              All ({items.length})
            </button>
            <button
              onClick={() => setFilter('image')}
              className={`px-6 py-2 rounded-lg font-semibold transition-colors flex items-center space-x-2 ${
                filter === 'image' 
                  ? 'bg-red-600 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <ImageIcon className="h-4 w-4" />
              <span>Photos ({items.filter(i => i.media_type === 'image').length})</span>
            </button>
            <button
              onClick={() => setFilter('video')}
              className={`px-6 py-2 rounded-lg font-semibold transition-colors flex items-center space-x-2 ${
                filter === 'video' 
                  ? 'bg-red-600 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <Video className="h-4 w-4" />
              <span>Videos ({items.filter(i => i.media_type === 'video').length})</span>
            </button>
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          {filteredItems.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-600 text-lg">No items to display</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {filteredItems.map((item) => (
                <Card 
                  key={item.id} 
                  className="cursor-pointer hover:shadow-xl transition-shadow overflow-hidden group"
                  onClick={() => setSelectedItem(item)}
                >
                  <div className="relative aspect-square bg-gray-200">
                    {item.media_type === 'image' ? (
                      <img 
                        src={item.media_url} 
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                        {item.thumbnail_url ? (
                          <img 
                            src={item.thumbnail_url} 
                            alt={item.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <Video className="h-16 w-16 text-white" />
                        )}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="bg-red-600 rounded-full p-4">
                            <Video className="h-8 w-8 text-white" />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-gray-900 line-clamp-2">
                      {item.title}
                    </h3>
                    {item.description && (
                      <p className="text-sm text-gray-600 line-clamp-2 mt-1">
                        {item.description}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <Lightbox item={selectedItem} onClose={() => setSelectedItem(null)} />
    </div>
  );
};

export default GalleryPage;
